/**
 * FERP Portal - Environment Access Utilities
 *
 * Typed utilities for accessing runtime environment configuration.
 * Eliminates `(window as any).__ENV__` patterns throughout the codebase.
 *
 * Usage:
 * ```typescript
 * import { getEnv, isMockAuthEnabled, getOidcConfig } from '@/utils/env';
 *
 * const env = getEnv();
 * if (isMockAuthEnabled()) { ... }
 * const { issuer, clientId } = getOidcConfig();
 * ```
 */

// Import the global type declarations (side effect import ensures types are available)
import '../types/global.d.ts';

/**
 * Get the runtime environment configuration.
 * Returns an empty object if not available (SSR or not injected).
 */
export function getEnv(): Readonly<NonNullable<typeof window.__ENV__>> {
  if (typeof window !== 'undefined' && window.__ENV__) {
    return window.__ENV__;
  }
  return {};
}

/**
 * Get a specific environment variable with optional default.
 * Returns string representation of the value.
 */
export function getEnvVar(key: string, defaultValue?: string): string | undefined {
  const env = getEnv();
  const value = env[key];
  if (value === undefined) return defaultValue;
  return typeof value === 'boolean' ? String(value) : value;
}

/**
 * Check if mock auth mode is enabled (local development).
 */
export function isMockAuthEnabled(): boolean {
  return getEnv().ENABLE_MOCK_AUTH === 'true';
}

/**
 * Check if dev mode is enabled.
 */
export function isDevModeEnabled(): boolean {
  return getEnv().ENABLE_DEV_MODE === 'true';
}

/**
 * Get the configured tenant ID (if any).
 * Returns undefined if not configured (multi-tenant mode).
 */
export function getConfiguredTenant(): string | undefined {
  if (typeof window !== 'undefined') {
    return window.__TENANT__;
  }
  return undefined;
}

/**
 * Get OIDC configuration from runtime env.
 */
export function getOidcConfig(): {
  issuer: string | undefined;
  clientId: string | undefined;
  redirectUri: string | undefined;
  scopes: string | undefined;
} {
  const env = getEnv();

  // Window overrides take precedence over env
  const issuer =
    (typeof window !== 'undefined' ? window.__OIDC_ISSUER__ : undefined) ??
    env.OIDC_ISSUER;

  const clientId =
    (typeof window !== 'undefined' ? window.__OIDC_CLIENT_ID__ : undefined) ??
    env.OIDC_CLIENT_ID;

  const redirectUri =
    (typeof window !== 'undefined' ? window.__OIDC_REDIRECT_URI__ : undefined) ??
    env.OIDC_REDIRECT_URI ??
    (typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : undefined);

  const scopesRaw =
    (typeof window !== 'undefined' ? window.__REQUIRED_SCOPES__ : undefined) ??
    env.OIDC_SCOPES;

  const scopes = Array.isArray(scopesRaw) ? scopesRaw.join(' ') : scopesRaw;

  return { issuer, clientId, redirectUri, scopes };
}

/**
 * Check if AWS STS should be skipped (dev mode only).
 */
export function shouldSkipAwsSts(): boolean {
  if (typeof window !== 'undefined' && window.__SKIP_AWS_STS__) {
    return true;
  }
  return false;
}

/**
 * Get ID token — delegates to @ferp/auth (module-scoped, never on globalThis).
 */
export function getGlobalIdToken(): string | undefined {
  // Kept the function name for call-site compatibility; reads from @ferp/auth.
  try {
    return sessionStorage.getItem('auth:id_token') || undefined;
  } catch {
    return undefined;
  }
}

/**
 * @deprecated — tokens are managed by @ferp/auth, not globalThis.
 */
export function setGlobalIdToken(_token: string | undefined): void {
  // no-op: @ferp/auth owns token lifecycle
}

/**
 * Get auth token — delegates to @ferp/auth (module-scoped, never on globalThis).
 */
export function getGlobalAuthToken(): string | undefined {
  try {
    return sessionStorage.getItem('auth:token') || undefined;
  } catch {
    return undefined;
  }
}

/**
 * @deprecated — tokens are managed by @ferp/auth, not globalThis.
 */
export function setGlobalAuthToken(_token: string | undefined): void {
  // no-op: @ferp/auth owns token lifecycle
}

/**
 * Update runtime environment (for testing/mocking).
 * Only works if window.__ENV__ exists.
 */
export function updateEnv(updates: Partial<NonNullable<typeof window.__ENV__>>): void {
  if (typeof window !== 'undefined') {
    window.__ENV__ = {
      ...window.__ENV__,
      ...updates,
    };
  }
}
